/**
 * The fighter HUD: collimated green symbology, drawn over whatever is being flown.
 *
 * ## Collimated, and why that is the whole design
 *
 * A real head-up display is focused at infinity, so its symbols sit *on* the outside world rather
 * than on a piece of glass in front of it. The consequence that matters here is that the
 * symbology is **angularly correct**: the flight path marker is drawn where the aircraft is
 * actually going, so it lands on the point of the runway the aeroplane will arrive at; the pitch
 * ladder's 5° rung is drawn 5° above the horizon and therefore *on* the horizon line the terrain
 * draws. Get that wrong — draw the ladder on a fixed pixel scale — and the HUD is a decoration
 * that happens to have numbers on it.
 *
 * So everything here is projected through the camera's own field of view. One number,
 * `pixelsPerRadian`, ties the symbology to the scene, and it is recomputed whenever the field of
 * view or the viewport changes.
 *
 * ## Why a 2D canvas over the scene rather than geometry in it
 *
 * A HUD is thin bright lines, and thin bright lines are exactly what a rasteriser is worst at:
 * as geometry they alias into a shimmering mess unless they are wide enough to stop looking like
 * a HUD. A 2D canvas gets analytic anti-aliasing for free, costs one composited layer, and its
 * text is real text rather than a signed-distance-field approximation of it. The cost is that it
 * cannot be occluded by the world — which is correct, because a real HUD cannot either.
 *
 * ## What is drawn
 *
 * The standard set: flight path marker, pitch ladder, airspeed and altitude tapes, heading tape,
 * angle-of-attack bracket, G readout, bank scale. Nothing invented. A pilot who has used one
 * should be able to read this one without being told anything.
 *
 * `updateFrame(alpha)`: yes — {@link FighterHud.updateFrame}, self-throttled. Allocation-free per
 * frame after construction.
 */

import { DEG2RAD, RAD2DEG, clamp } from '../core/math.js';
import { metresToFeet, mpsToKnots } from '../core/units.js';
import type { FlightState } from '../sim/fdm.js';

/** HUD phosphor green. The colour is a real one — P43 phosphor, as used on every HUD ever built. */
const GREEN = '#41ff7a';
const GREEN_DIM = 'rgba(65, 255, 122, 0.55)';
const GREEN_FAINT = 'rgba(65, 255, 122, 0.28)';
/** The one colour that is not phosphor green: a warning, and nothing else. */
const WARN = '#ff5a3c';

/**
 * The weapons half of the HUD.
 *
 * `null` means **cold** — no master arm, no stores, nothing to draw. That is the whole of the
 * "free flight has zero weapons UI" requirement: the caller passes `null` and this file's weapons
 * layer never runs. It is not a visibility flag that somebody can forget to set, because there is
 * nothing to set it *from* unless an armed weapon system exists.
 */
export interface WeaponsHudState {
  selection: 'guns' | 'missile';
  /** Rounds left. */
  ammunition: number;
  /** Missiles on the rails. */
  missiles: number;
  /** Pipper offset from the boresight [rad]; right and down positive. */
  pipperAzimuthRad: number;
  pipperElevationRad: number;
  /** Range the sight is solving for [m]. */
  gunRangeM: number;
  seekerStatus: 'none' | 'tracking' | 'locked';
  /** 0…1 while acquiring. */
  lockProgress: number;
  /** Range to the tracked target [m], 0 for none. */
  targetRangeM: number;
  /** Closing speed [m/s]. */
  targetClosureMps: number;
  /**
   * Where the tracked target is, as an angle off the boresight [rad]. `NaN` when nothing is
   * tracked, which is what suppresses the designator box.
   */
  targetAzimuthRad: number;
  targetElevationRad: number;
  /** Is the shot inside the launch envelope? */
  inRange: boolean;
  /** Live score. */
  score: number;
  targetsRemaining: number;
  elapsedS: number;
}

export function makeWeaponsHudState(): WeaponsHudState {
  return {
    selection: 'guns',
    ammunition: 0,
    missiles: 0,
    pipperAzimuthRad: 0,
    pipperElevationRad: 0,
    gunRangeM: 0,
    seekerStatus: 'none',
    lockProgress: 0,
    targetRangeM: 0,
    targetClosureMps: 0,
    targetAzimuthRad: Number.NaN,
    targetElevationRad: Number.NaN,
    inRange: false,
    score: 0,
    targetsRemaining: 0,
    elapsedS: 0,
  };
}

/** Redraw rate [Hz]. Faster than the steam gauges: a HUD has no needle inertia to hide behind. */
const REFRESH_HZ = 60;

export interface HudInputs {
  indicatedKt: number;
  altitudeFt: number;
  headingDeg: number;
  pitchDeg: number;
  rollDeg: number;
  /** Angle of attack [deg]. */
  alphaDeg: number;
  /** Sideslip [deg], positive nose-left. */
  betaDeg: number;
  loadFactor: number;
  /** Flight path angle [deg], positive climbing. */
  flightPathDeg: number;
  machNumber: number;
  /** Alpha at which the wing stalls [deg] — the top of the AoA bracket. */
  stallAlphaDeg: number;
  /** Alpha for best approach speed [deg] — the bracket's on-speed notch. */
  approachAlphaDeg: number;
  onGround: boolean;
}

export function makeHudInputs(): HudInputs {
  return {
    indicatedKt: 0,
    altitudeFt: 0,
    headingDeg: 0,
    pitchDeg: 0,
    rollDeg: 0,
    alphaDeg: 0,
    betaDeg: 0,
    loadFactor: 1,
    flightPathDeg: 0,
    machNumber: 0,
    stallAlphaDeg: 17,
    approachAlphaDeg: 11,
    onGround: false,
  };
}

/** Read the HUD from the flight model. The one place SI becomes aviation units. */
export function readHud(
  state: FlightState,
  out: HudInputs,
  limits: { stallAlphaDeg: number; approachAlphaDeg: number },
): HudInputs {
  out.indicatedKt = mpsToKnots(state.indicatedAirspeedMps);
  out.altitudeFt = metresToFeet(state.altitudeM);
  out.headingDeg = ((state.headingRad * RAD2DEG) % 360 + 360) % 360;
  out.pitchDeg = state.pitchRad * RAD2DEG;
  out.rollDeg = state.rollRad * RAD2DEG;
  out.alphaDeg = state.alphaRad * RAD2DEG;
  out.betaDeg = state.betaRad * RAD2DEG;
  out.loadFactor = state.loadFactor;
  out.flightPathDeg = state.flightPathAngleRad * RAD2DEG;
  out.machNumber = state.machNumber;
  out.stallAlphaDeg = limits.stallAlphaDeg;
  out.approachAlphaDeg = limits.approachAlphaDeg;
  out.onGround = state.onGround;
  return out;
}

export class FighterHud {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private widthPx = 0;
  private heightPx = 0;
  private devicePixelRatioValue = 1;
  /**
   * Pixels per radian of view angle — what makes the symbology collimated.
   *
   * Derived from the camera's vertical field of view and the viewport height, which is the exact
   * relationship the perspective projection uses. Everything angular on this HUD is multiplied by
   * it, so the pitch ladder sits on the real horizon at any field of view and on any panel.
   */
  private pixelsPerRadian = 500;

  private nextUpdateMs = 0;
  private visibleValue = false;
  /** Null when the aircraft is cold. See {@link WeaponsHudState}. */
  private weapons: WeaponsHudState | null = null;

  constructor(parent: HTMLElement = document.body) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hud-overlay';
    this.canvas.hidden = true;
    const ctx = this.canvas.getContext('2d');
    if (ctx === null) throw new Error('2D canvas context unavailable — the HUD cannot be drawn');
    this.ctx = ctx;
    parent.append(this.canvas);
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.canvas.hidden = !visible;
    this.nextUpdateMs = 0;
  }

  toggle(): boolean {
    this.setVisible(!this.visibleValue);
    return this.visibleValue;
  }

  /**
   * Match the viewport, and take the camera's field of view.
   *
   * Both, together, because either one alone changes the pixels-per-radian and a HUD that knew
   * about one but not the other would be right at exactly one setting.
   */
  setViewport(widthCss: number, heightCss: number, verticalFovDeg: number, pixelRatio = 1): void {
    this.widthPx = Math.max(1, Math.round(widthCss));
    this.heightPx = Math.max(1, Math.round(heightCss));
    this.devicePixelRatioValue = clamp(pixelRatio, 1, 3);

    this.canvas.width = Math.round(this.widthPx * this.devicePixelRatioValue);
    this.canvas.height = Math.round(this.heightPx * this.devicePixelRatioValue);
    this.canvas.style.width = `${this.widthPx}px`;
    this.canvas.style.height = `${this.heightPx}px`;

    // The projection's own relationship: half the viewport subtends half the field of view.
    const halfFov = clamp(verticalFovDeg, 10, 140) * 0.5 * DEG2RAD;
    this.pixelsPerRadian = this.heightPx / 2 / Math.tan(halfFov);
    this.nextUpdateMs = 0;
  }

  /** Pixels per degree of view angle. Exposed so tests can check the collimation directly. */
  get pixelsPerDegree(): number {
    return this.pixelsPerRadian * DEG2RAD;
  }

  /** Redraw, at most {@link REFRESH_HZ} times a second. Returns true when it drew. */
  updateFrame(nowMs: number, inputs: HudInputs): boolean {
    if (!this.visibleValue || nowMs < this.nextUpdateMs || this.widthPx === 0) return false;
    this.nextUpdateMs = nowMs + 1000 / REFRESH_HZ;

    const ctx = this.ctx;
    const dpr = this.devicePixelRatioValue;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.widthPx, this.heightPx);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.textBaseline = 'middle';

    const cx = this.widthPx / 2;
    const cy = this.heightPx / 2;

    this.drawPitchLadder(inputs, cx, cy);
    this.drawBankScale(inputs, cx, cy);
    this.drawFlightPathMarker(inputs, cx, cy);
    this.drawAirspeedTape(inputs, cx, cy);
    this.drawAltitudeTape(inputs, cx, cy);
    this.drawHeadingTape(inputs, cx);
    this.drawAoaBracket(inputs, cx, cy);
    this.drawReadouts(inputs, cx, cy);
    if (this.weapons !== null) this.drawWeapons(this.weapons, cx, cy);
    return true;
  }

  /**
   * Hand the HUD the armament's state, or `null` for a cold aircraft.
   *
   * The state object is the caller's and is read, not retained beyond the frame — it is the same
   * reused struct everything else in the frame path uses.
   */
  setWeapons(state: WeaponsHudState | null): void {
    this.weapons = state;
  }

  /**
   * The flight path marker: where the aeroplane is *going*, not where it is pointing.
   *
   * The single most useful symbol on a HUD, and the one that makes the rest of it worth having.
   * It sits at the velocity vector's angle off the nose — down by the difference between pitch
   * attitude and flight path angle, and sideways by the sideslip — which means that on approach
   * you put it on the touchdown zone and the aeroplane goes there.
   */
  private drawFlightPathMarker(inputs: HudInputs, cx: number, cy: number): void {
    const ctx = this.ctx;
    const perDeg = this.pixelsPerDegree;
    // Depression below the nose is the difference between where it points and where it goes.
    const x = cx + clamp(-inputs.betaDeg, -18, 18) * perDeg;
    const y = cy + clamp(inputs.pitchDeg - inputs.flightPathDeg, -22, 22) * perDeg;

    const radius = Math.max(9, this.heightPx * 0.014);
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    // Wings and fin, the standard "aeroplane seen from behind" glyph.
    ctx.moveTo(x - radius, y);
    ctx.lineTo(x - radius * 2.3, y);
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + radius * 2.3, y);
    ctx.moveTo(x, y - radius);
    ctx.lineTo(x, y - radius * 1.9);
    ctx.stroke();
  }

  /**
   * The pitch ladder, drawn in the horizon's frame.
   *
   * Rotated by bank and translated by pitch, both at the collimated scale, so the zero rung *is*
   * the horizon. Climb rungs are solid, descent rungs dashed and their ends turned down — the
   * convention that lets a pilot tell up from down when the ladder is the only thing visible,
   * which is the situation it exists for.
   */
  private drawPitchLadder(inputs: HudInputs, cx: number, cy: number): void {
    const ctx = this.ctx;
    const perDeg = this.pixelsPerDegree;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-inputs.rollDeg * DEG2RAD);
    ctx.translate(0, inputs.pitchDeg * perDeg);
    ctx.font = `600 ${Math.round(this.heightPx * 0.017)}px ui-monospace, monospace`;

    const halfWidth = Math.max(50, this.widthPx * 0.085);
    const gap = halfWidth * 0.42;

    for (let deg = -60; deg <= 60; deg += 5) {
      const y = -deg * perDeg;
      // Off the top or bottom of the screen: skip rather than stroke into the clip.
      if (Math.abs(y) > this.heightPx * 0.9) continue;

      if (deg === 0) {
        // The horizon: a full-width line with a gap for the boresight.
        ctx.strokeStyle = GREEN;
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(-this.widthPx * 0.42, 0);
        ctx.lineTo(-gap, 0);
        ctx.moveTo(gap, 0);
        ctx.lineTo(this.widthPx * 0.42, 0);
        ctx.stroke();
        continue;
      }

      const climbing = deg > 0;
      ctx.strokeStyle = climbing ? GREEN : GREEN_DIM;
      ctx.lineWidth = 1.6;
      ctx.setLineDash(climbing ? [] : [7, 6]);

      ctx.beginPath();
      for (const side of [-1, 1]) {
        const inner = side * gap;
        const outer = side * halfWidth;
        ctx.moveTo(inner, y);
        ctx.lineTo(outer, y);
        // The turned-down (or up) end: the cue that says which way the ground is.
        ctx.lineTo(outer, y + (climbing ? perDeg * 0.9 : -perDeg * 0.9));
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // The rung's own number, upright at both ends.
      ctx.fillStyle = climbing ? GREEN : GREEN_DIM;
      ctx.textAlign = 'right';
      ctx.fillText(String(Math.abs(deg)), -halfWidth - 6, y);
      ctx.textAlign = 'left';
      ctx.fillText(String(Math.abs(deg)), halfWidth + 6, y);
    }

    ctx.restore();

    // The boresight — the aircraft's own nose, fixed to the airframe and therefore not rotated.
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 2;
    const arm = Math.max(10, this.widthPx * 0.016);
    ctx.beginPath();
    ctx.moveTo(cx - arm * 2, cy);
    ctx.lineTo(cx - arm * 0.6, cy);
    ctx.moveTo(cx + arm * 0.6, cy);
    ctx.lineTo(cx + arm * 2, cy);
    ctx.moveTo(cx, cy - arm * 0.6);
    ctx.lineTo(cx, cy + arm * 0.6);
    ctx.stroke();
  }

  /** Bank scale: a fixed arc of ticks with a pointer that rolls with the aeroplane. */
  private drawBankScale(inputs: HudInputs, cx: number, cy: number): void {
    const ctx = this.ctx;
    const radius = this.heightPx * 0.33;

    ctx.strokeStyle = GREEN_DIM;
    ctx.lineWidth = 1.6;
    for (const bankDeg of [-45, -30, -20, -10, 0, 10, 20, 30, 45]) {
      const angle = (-90 + bankDeg) * DEG2RAD;
      const major = bankDeg % 30 === 0 || bankDeg === 0;
      const length = major ? 12 : 7;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(cx + cos * radius, cy + sin * radius);
      ctx.lineTo(cx + cos * (radius + length), cy + sin * (radius + length));
      ctx.stroke();
    }

    // The pointer sits at the current bank and points inward — the "sky pointer" convention.
    const angle = (-90 - inputs.rollDeg) * DEG2RAD;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    ctx.fillStyle = GREEN;
    ctx.beginPath();
    ctx.moveTo(cx + cos * (radius - 2), cy + sin * (radius - 2));
    ctx.lineTo(cx + cos * (radius - 15) - sin * 7, cy + sin * (radius - 15) + cos * 7);
    ctx.lineTo(cx + cos * (radius - 15) + sin * 7, cy + sin * (radius - 15) - cos * 7);
    ctx.closePath();
    ctx.fill();
  }

  /** Airspeed tape, left of centre, with a boxed digital readout at the pointer. */
  private drawAirspeedTape(inputs: HudInputs, cx: number, cy: number): void {
    this.drawTape({
      value: inputs.indicatedKt,
      x: cx - this.widthPx * 0.24,
      cy,
      side: -1,
      step: 10,
      minorStep: 5,
      pixelsPerUnit: this.heightPx * 0.0075,
      format: (v) => String(Math.round(v)),
      boxFormat: (v) => String(Math.max(0, Math.round(v))).padStart(3, ' '),
      label: 'KIAS',
    });
  }

  /** Altitude tape, right of centre. */
  private drawAltitudeTape(inputs: HudInputs, cx: number, cy: number): void {
    this.drawTape({
      value: inputs.altitudeFt,
      x: cx + this.widthPx * 0.24,
      cy,
      side: 1,
      step: 500,
      minorStep: 100,
      pixelsPerUnit: this.heightPx * 0.00016,
      format: (v) => String(Math.round(v / 100)),
      boxFormat: (v) => String(Math.round(v)),
      label: 'FEET',
    });
  }

  /**
   * A moving tape.
   *
   * Shared by airspeed and altitude because they are the same instrument with different scales,
   * and writing it once is what keeps them looking like a matched pair — which is most of what
   * makes a HUD readable at a glance.
   */
  private drawTape(spec: {
    value: number;
    x: number;
    cy: number;
    side: -1 | 1;
    step: number;
    minorStep: number;
    pixelsPerUnit: number;
    format: (value: number) => string;
    boxFormat: (value: number) => string;
    label: string;
  }): void {
    const ctx = this.ctx;
    const half = this.heightPx * 0.16;
    const fontPx = Math.round(this.heightPx * 0.019);
    ctx.font = `600 ${fontPx}px ui-monospace, monospace`;

    ctx.save();
    ctx.beginPath();
    ctx.rect(spec.x - 70, spec.cy - half, 140, half * 2);
    ctx.clip();

    const first = Math.floor((spec.value - half / spec.pixelsPerUnit) / spec.minorStep) * spec.minorStep;
    const last = spec.value + half / spec.pixelsPerUnit;
    for (let v = first; v <= last; v += spec.minorStep) {
      const y = spec.cy - (v - spec.value) * spec.pixelsPerUnit;
      const major = Math.abs(v % spec.step) < 1e-6;
      ctx.strokeStyle = major ? GREEN : GREEN_FAINT;
      ctx.lineWidth = major ? 1.8 : 1.2;
      ctx.beginPath();
      ctx.moveTo(spec.x, y);
      ctx.lineTo(spec.x + spec.side * (major ? 14 : 8), y);
      ctx.stroke();
      if (major) {
        ctx.fillStyle = GREEN;
        ctx.textAlign = spec.side < 0 ? 'right' : 'left';
        ctx.fillText(spec.format(v), spec.x + spec.side * 20, y);
      }
    }
    ctx.restore();

    // The pointer box, at the centre, showing the exact value.
    const boxW = 74;
    const boxH = fontPx + 12;
    const boxX = spec.x - spec.side * (boxW + 4) - (spec.side < 0 ? 0 : 0);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.fillRect(boxX - boxW / 2, spec.cy - boxH / 2, boxW, boxH);
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 1.8;
    ctx.strokeRect(boxX - boxW / 2, spec.cy - boxH / 2, boxW, boxH);
    // A caret pointing at the tape.
    ctx.beginPath();
    const caretX = boxX + spec.side * (boxW / 2);
    ctx.moveTo(caretX, spec.cy - 7);
    ctx.lineTo(caretX + spec.side * 9, spec.cy);
    ctx.lineTo(caretX, spec.cy + 7);
    ctx.stroke();

    ctx.fillStyle = GREEN;
    ctx.textAlign = 'center';
    ctx.fillText(spec.boxFormat(spec.value), boxX, spec.cy);

    ctx.font = `600 ${Math.round(fontPx * 0.72)}px ui-monospace, monospace`;
    ctx.fillStyle = GREEN_DIM;
    ctx.fillText(spec.label, boxX, spec.cy - half - 10);
  }

  /** Heading tape across the top, with a caret at the current heading. */
  private drawHeadingTape(inputs: HudInputs, cx: number): void {
    const ctx = this.ctx;
    const y = this.heightPx * 0.10;
    const halfWidth = this.widthPx * 0.20;
    const perDeg = halfWidth / 30; // ±30° visible
    const fontPx = Math.round(this.heightPx * 0.018);

    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - halfWidth - 24, y - 24, (halfWidth + 24) * 2, 48);
    ctx.clip();
    ctx.font = `600 ${fontPx}px ui-monospace, monospace`;
    ctx.textAlign = 'center';

    const first = Math.floor((inputs.headingDeg - 32) / 5) * 5;
    for (let deg = first; deg <= inputs.headingDeg + 32; deg += 5) {
      const x = cx + (deg - inputs.headingDeg) * perDeg;
      const major = ((deg % 10) + 10) % 10 === 0;
      ctx.strokeStyle = major ? GREEN : GREEN_FAINT;
      ctx.lineWidth = major ? 1.8 : 1.2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + (major ? 11 : 6));
      ctx.stroke();
      if (major) {
        const wrapped = ((deg % 360) + 360) % 360;
        ctx.fillStyle = GREEN;
        ctx.fillText(String(wrapped / 10).padStart(2, '0'), x, y - 10);
      }
    }
    ctx.restore();

    ctx.fillStyle = GREEN;
    ctx.beginPath();
    ctx.moveTo(cx, y + 16);
    ctx.lineTo(cx - 7, y + 27);
    ctx.lineTo(cx + 7, y + 27);
    ctx.closePath();
    ctx.fill();
  }

  /**
   * The angle-of-attack bracket, left of the flight path marker.
   *
   * The one symbol that is *not* a number: a bracket with a caret that slides up it, on-speed
   * when the caret sits in the notch. Which is the point — on approach a pilot flies the caret,
   * not the airspeed, because alpha is what stalls a wing and airspeed is only a proxy for it at
   * one weight and one load factor.
   */
  private drawAoaBracket(inputs: HudInputs, cx: number, cy: number): void {
    const ctx = this.ctx;
    const x = cx - this.widthPx * 0.115;
    const half = this.heightPx * 0.075;

    ctx.strokeStyle = GREEN_DIM;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(x + 10, cy - half);
    ctx.lineTo(x, cy - half);
    ctx.lineTo(x, cy + half);
    ctx.lineTo(x + 10, cy + half);
    ctx.stroke();

    // The on-speed notch, at the approach alpha.
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, cy);
    ctx.lineTo(x + 14, cy);
    ctx.stroke();

    /*
     * The caret's position maps alpha onto the bracket with the *approach* alpha in the middle
     * and the stall alpha at the top. So "caret above the notch" means slower than on-speed and
     * closer to the stall, which is the direction a pilot expects — and the top of the bracket is
     * the stall, not an arbitrary limit.
     */
    const span = Math.max(1, inputs.stallAlphaDeg - inputs.approachAlphaDeg);
    const offset = clamp((inputs.alphaDeg - inputs.approachAlphaDeg) / span, -1.4, 1.4);
    const caretY = cy - offset * half;
    const stalling = inputs.alphaDeg >= inputs.stallAlphaDeg;

    ctx.strokeStyle = stalling ? '#ff5a3c' : GREEN;
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(x - 11, caretY);
    ctx.lineTo(x - 3, caretY - 7);
    ctx.moveTo(x - 11, caretY);
    ctx.lineTo(x - 3, caretY + 7);
    ctx.stroke();
  }

  /** The corner readouts: G, Mach, and the aircraft's state. */
  private drawReadouts(inputs: HudInputs, cx: number, cy: number): void {
    const ctx = this.ctx;
    const fontPx = Math.round(this.heightPx * 0.019);
    ctx.font = `600 ${fontPx}px ui-monospace, monospace`;
    ctx.textAlign = 'left';

    const left = cx - this.widthPx * 0.24 - 40;
    // G below the airspeed tape, which is where a fighter HUD puts it.
    ctx.fillStyle = Math.abs(inputs.loadFactor) > 3.8 ? '#ff5a3c' : GREEN;
    ctx.fillText(`${inputs.loadFactor >= 0 ? ' ' : ''}${inputs.loadFactor.toFixed(1)}G`, left, cy + this.heightPx * 0.20);

    if (inputs.machNumber >= 0.3) {
      ctx.fillStyle = GREEN;
      ctx.fillText(`M ${inputs.machNumber.toFixed(2)}`, left, cy + this.heightPx * 0.20 + fontPx * 1.6);
    }

    // Alpha, numerically, beside the bracket — the bracket is for flying, the number for checking.
    ctx.fillStyle = GREEN_DIM;
    ctx.textAlign = 'right';
    ctx.fillText(`α ${inputs.alphaDeg.toFixed(1)}°`, cx - this.widthPx * 0.13, cy + this.heightPx * 0.095);

    if (inputs.onGround) {
      ctx.fillStyle = GREEN_DIM;
      ctx.textAlign = 'center';
      ctx.fillText('ON GROUND', cx, cy + this.heightPx * 0.30);
    }
  }

  // ---------------------------------------------------------------------------------------
  // Weapons
  // ---------------------------------------------------------------------------------------

  /**
   * The armament layer.
   *
   * Everything here is drawn in the same collimated space as the rest of the HUD — the pipper and
   * the target box are placed by *angle*, through `pixelsPerRadian`, so the pipper genuinely sits
   * where the rounds are going and the box genuinely sits on the target. A pipper placed at a
   * fixed pixel offset would be a decoration that happened to have a gun behind it.
   */
  private drawWeapons(state: WeaponsHudState, cx: number, cy: number): void {
    if (state.selection === 'guns') this.drawGunSight(state, cx, cy);
    else this.drawSeekerReticle(state, cx, cy);

    this.drawTargetBox(state, cx, cy);
    this.drawStores(state, cx, cy);
  }

  /**
   * The gun cross and the LCOS pipper.
   *
   * Two symbols, and the distinction between them is the entire idea of a lead-computing sight.
   * The **cross** is the boresight: it is welded to the aeroplane and marks where the barrel
   * points. The **pipper** is where the rounds will actually be, which in any turn is somewhere
   * else entirely. You aim with the pipper. The cross is there so you can see how much lead you
   * are pulling, and it is genuinely useful — the gap between them is the shot's difficulty.
   */
  private drawGunSight(state: WeaponsHudState, cx: number, cy: number): void {
    const ctx = this.ctx;
    const perRad = this.pixelsPerRadian;

    // --- the fixed gun cross -----------------------------------------------------------------
    const arm = Math.max(7, this.widthPx * 0.011);
    ctx.strokeStyle = GREEN_DIM;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(cx - arm, cy);
    ctx.lineTo(cx + arm, cy);
    ctx.moveTo(cx, cy - arm);
    ctx.lineTo(cx, cy + arm);
    ctx.stroke();

    // --- the pipper --------------------------------------------------------------------------
    // Clamped to the glass: a solution 40° off the nose is real, and drawing it off-screen would
    // just make the pipper vanish exactly when the pilot most wants to know where it went.
    const limit = 0.32;
    const px = cx + clamp(state.pipperAzimuthRad, -limit, limit) * perRad;
    const py = cy + clamp(state.pipperElevationRad, -limit, limit) * perRad;
    const radius = Math.max(8, this.heightPx * 0.013);

    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.stroke();
    // The aiming dot in the middle of it.
    ctx.fillStyle = GREEN;
    ctx.beginPath();
    ctx.arc(px, py, 2, 0, Math.PI * 2);
    ctx.fill();

    /*
     * The range bar: a clock hand around the pipper, running anticlockwise from the top as the
     * range closes. The universal gunsight convention, and it lets a pilot read range without
     * looking away from the target.
     */
    const fraction = clamp(1 - state.gunRangeM / 1_500, 0, 1);
    ctx.strokeStyle = GREEN;
    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.arc(px, py, radius + 5, -Math.PI / 2, -Math.PI / 2 + fraction * Math.PI * 2);
    ctx.stroke();

    ctx.font = `600 ${Math.round(this.heightPx * 0.016)}px ui-monospace, monospace`;
    ctx.fillStyle = GREEN_DIM;
    ctx.textAlign = 'left';
    ctx.fillText(`${Math.round(state.gunRangeM)}`, px + radius + 12, py);
  }

  /**
   * The seeker reticle: a circle that closes as the lock builds.
   *
   * The visual counterpart of the growl. Tracking, it is a dashed circle with an arc showing how
   * far through the acquisition it is; locked, it is solid and it stops moving. Out of the launch
   * envelope it goes red, because a lock you cannot shoot is worth knowing about.
   */
  private drawSeekerReticle(state: WeaponsHudState, cx: number, cy: number): void {
    const ctx = this.ctx;
    const radius = Math.max(18, this.heightPx * 0.05);
    const locked = state.seekerStatus === 'locked';
    const colour = locked ? (state.inRange ? GREEN : WARN) : GREEN_DIM;

    ctx.strokeStyle = colour;
    ctx.lineWidth = locked ? 2.4 : 1.6;
    ctx.setLineDash(locked ? [] : [5, 7]);
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    if (state.seekerStatus === 'tracking' && state.lockProgress > 0) {
      // The acquisition arc, filling clockwise from the top.
      ctx.strokeStyle = GREEN;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 6, -Math.PI / 2, -Math.PI / 2 + state.lockProgress * Math.PI * 2);
      ctx.stroke();
    }

    if (locked) {
      ctx.font = `700 ${Math.round(this.heightPx * 0.018)}px ui-monospace, monospace`;
      ctx.fillStyle = colour;
      ctx.textAlign = 'center';
      ctx.fillText(state.inRange ? 'LOCK' : 'RANGE', cx, cy - radius - 14);
    }
  }

  /**
   * The target designator: a box around whatever the seeker is holding, with range and closure.
   *
   * Placed by angle like everything else, so it sits *on* the target in the world. Suppressed when
   * nothing is tracked — the caller signals that with `NaN`, which is the only value that cannot
   * be confused with a real bearing of zero (a target dead ahead).
   */
  private drawTargetBox(state: WeaponsHudState, cx: number, cy: number): void {
    if (!Number.isFinite(state.targetAzimuthRad) || !Number.isFinite(state.targetElevationRad)) {
      return;
    }
    const ctx = this.ctx;
    const perRad = this.pixelsPerRadian;
    const limit = 0.55;
    const x = cx + clamp(state.targetAzimuthRad, -limit, limit) * perRad;
    const y = cy + clamp(state.targetElevationRad, -limit, limit) * perRad;
    const half = Math.max(10, this.heightPx * 0.02);

    ctx.strokeStyle = state.seekerStatus === 'locked' ? GREEN : GREEN_DIM;
    ctx.lineWidth = 2;
    // Corner ticks rather than a closed box: it frames the target without hiding it.
    const tick = half * 0.45;
    ctx.beginPath();
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        ctx.moveTo(x + sx * half, y + sy * half - sy * tick);
        ctx.lineTo(x + sx * half, y + sy * half);
        ctx.lineTo(x + sx * half - sx * tick, y + sy * half);
      }
    }
    ctx.stroke();

    if (state.targetRangeM > 0) {
      ctx.font = `600 ${Math.round(this.heightPx * 0.015)}px ui-monospace, monospace`;
      ctx.fillStyle = GREEN;
      ctx.textAlign = 'center';
      // Range in thousands of feet is the fighter convention, and closure beside it.
      ctx.fillText(
        `${(state.targetRangeM / 1_000).toFixed(1)}k  ${state.targetClosureMps >= 0 ? '+' : ''}${Math.round(
          state.targetClosureMps,
        )}`,
        x,
        y + half + 14,
      );
    }
  }

  /** Stores, score and the clock, bottom left — out of the way of the flying. */
  private drawStores(state: WeaponsHudState, cx: number, cy: number): void {
    const ctx = this.ctx;
    const fontPx = Math.round(this.heightPx * 0.019);
    ctx.font = `600 ${fontPx}px ui-monospace, monospace`;
    ctx.textAlign = 'left';

    const x = cx - this.widthPx * 0.24 - 40;
    let y = cy + this.heightPx * 0.26;

    // The selected weapon is bright; the other is dim. One glance tells you what the trigger does.
    ctx.fillStyle = state.selection === 'guns' ? GREEN : GREEN_FAINT;
    ctx.fillText(`GUN ${String(state.ammunition).padStart(3, ' ')}`, x, y);
    y += fontPx * 1.5;
    ctx.fillStyle = state.selection === 'missile' ? GREEN : GREEN_FAINT;
    ctx.fillText(`MSL ${state.missiles}`, x, y);

    y += fontPx * 1.9;
    ctx.fillStyle = GREEN_DIM;
    ctx.fillText(`SCORE ${state.score}`, x, y);
    y += fontPx * 1.5;
    ctx.fillText(`TGT ${state.targetsRemaining}  ${state.elapsedS.toFixed(1)}s`, x, y);

    if (state.ammunition === 0 && state.missiles === 0) {
      ctx.fillStyle = WARN;
      ctx.textAlign = 'center';
      ctx.fillText('WINCHESTER', cx, cy + this.heightPx * 0.34);
    }
  }

  dispose(): void {
    this.canvas.remove();
  }
}
